/**
 * page-instance is the instance console: the tabbed operations surface for one
 * Sluice instance, routed at `/instances/:id`.
 *
 * Features: `SC-F319` (status and task view), `SC-F317` (power operations),
 * `SC-F320` (container log tail, v1), `SC-F323` (image allowlist and version
 * pinning), `SC-F332` (the one job resource), `SC-F823` (the live audit tail),
 * `SC-F916` (volume reclamation, which is what makes "deleted" true).
 * Stories: `SC-S316`, `SC-S317`, `SC-S318`, `SC-S322`, `SC-S323`, `SC-S813`.
 *
 * **The whole point of this page is that it does not lie about state.** The
 * instance record is desired state and Swarm is a projection of it (`SC-F300`),
 * so the two are rendered side by side and never fused into one green dot. A
 * paused Swarm update is invisible unless it is polled for (`02` §2.3) and it
 * never retries itself (`02` §9.4 D5) — `concept.md` §3.1 names "the UI shows
 * running for an instance whose Swarm update paused three hours ago" as the
 * exact failure that makes the platform-engineer persona churn. So: the
 * lifecycle state is rendered by its own name from `02` §2.1's eleven values,
 * an unhealthy rollout outranks the state name in the header, and an
 * observation the server calls stale (older than three reconciler passes) is
 * badged as such rather than presented as current.
 *
 * The tabs are in the URL fragment (`#tasks`, `#tail`, `#settings`, `#danger`)
 * so an operator can paste a colleague a link to the panel they are looking at,
 * without a route of their own — `router.ts` owns one path for this page.
 */

import { LitElement, css, html, nothing } from "lit";
import type { TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import {
  decisionVariant,
  toast,
  toastPersistent,
  type BnBadgeVariant,
  type BnColumn,
  type BnKvRow,
  type BnRow,
  type BnTab,
  type BnTabsChangeDetail,
} from "../components/index.js";

import {
  confirmStepUpOtp,
  listPasskeys,
  me,
  requestStepUpOtp,
  verifyWithPasskey,
} from "../api/auth.js";
import { getAuditTail, type AuditProjection, type TailRecord } from "../api/audit.js";
import { ApiError, errorDetail, parseError } from "../api/errors.js";
import { listGenerations, type Generation } from "../api/generations.js";
import {
  deleteInstance,
  getInstance,
  getInstanceHealth,
  getInstanceLogs,
  getInstanceStatus,
  restartInstance,
  resumeInstance,
  retryInstance,
  suspendInstance,
  type Instance,
  type InstanceHealth,
  type InstanceState,
  type InstanceStatus,
} from "../api/instances.js";
import { isTerminal, jobFailure, listInstanceJobs, watchJob, type Job } from "../api/jobs.js";
import { atLeast, type Role } from "../api/orgs.js";
import { passkeyFailureMessage, webauthnSupported } from "../api/webauthn.js";
import { hrefFor } from "../nav.js";

/* ── Tunables, each with the document that fixes it ──────────────────────── */

/** How often desired-versus-observed is re-read. */
const STATUS_POLL_MS = 5000;
/** `SC-F823`: the console polls `n=200` every 3 s and de-duplicates on the hash. */
const TAIL_POLL_MS = 3000;
const TAIL_N = 200;
/** The endpoint's own hard cap. Holding more client-side would imply the tail
 * remembers more than it does. */
const TAIL_CAP = 1000;

/* ── The lifecycle state machine, `02` §2.1 verbatim ──────────────────────
   All eleven states, because an instance is in exactly one of them and a UI
   that only knows "running" and "stopped" is the bug this page exists to
   prevent. There is deliberately no `stopped`: "we want it off" (`suspended`)
   and "it fell over" (`degraded` / `failed`) must be distinguishable by name. */

interface StateFacts {
  label: string;
  variant: BnBadgeVariant;
  /** Live states get the pulsing dot; terminal and idle ones do not. */
  dot: boolean;
  meaning: string;
}

const STATES: Record<InstanceState, StateFacts> = {
  draft: {
    label: "Draft",
    variant: "neutral",
    dot: false,
    meaning:
      "The record exists and nothing is deployed. Publishing the first generation provisions the instance.",
  },
  provisioning: {
    label: "Provisioning",
    variant: "info",
    dot: true,
    meaning: "Creating the network, volumes, configs and secrets, then the service.",
  },
  starting: {
    label: "Starting",
    variant: "info",
    dot: true,
    meaning:
      "The service exists. Waiting for a task to reach running and for readiness to answer 200. Readiness is fail-closed at boot, so a short 503 window here is expected and is not a failed rollout.",
  },
  healthy: {
    label: "Healthy",
    variant: "success",
    dot: true,
    meaning:
      "Desired matches observed, readiness answers 200, and the live config digest matches the live generation.",
  },
  degraded: {
    label: "Degraded",
    variant: "warning",
    dot: true,
    meaning:
      "A task is running and serving, but a subordinate signal is bad: readiness 503 because a datasource is unhealthy, a digest mismatch, or restarts below the crash-loop threshold.",
  },
  updating: {
    label: "Updating",
    variant: "info",
    dot: true,
    meaning:
      "A service update is in flight for a new generation, size or image. A revert to an older generation is also an ordinary update and appears here, not as a rollback.",
  },
  "rolling-back": {
    label: "Rolling back",
    variant: "warning",
    dot: true,
    meaning:
      "Not reached on this platform: Kubernetes runs no automatic rollback for an instance Pod. A revert to an older generation is an ordinary update and shows as Updating.",
  },
  suspended: {
    label: "Suspended",
    variant: "neutral",
    dot: false,
    meaning:
      "Deliberately stopped: the Pod is deleted. Not serving queries. The Service, the route, the Secrets and the data directory are retained and storage still bills.",
  },
  deleting: {
    label: "Deleting",
    variant: "warning",
    dot: true,
    meaning:
      "Teardown in progress: audit export, removal of the Pod, the Service, the Secrets and the route, then reclamation of the data directory. The record stays here until the directory is confirmed reclaimed.",
  },
  deleted: {
    label: "Deleted",
    variant: "neutral",
    dot: false,
    meaning:
      "Terminal. No Pod, Service, Secret or route remains and the data directory was confirmed reclaimed.",
  },
  failed: {
    label: "Failed",
    variant: "error",
    dot: true,
    meaning:
      "Provisioning or an update exhausted its retry budget, or a crash loop was detected. Automatic repair has stopped and this needs a person.",
  },
};

/** One thing wrong, ranked above the state name in the header. */
interface Alert {
  variant: BnBadgeVariant;
  badge: string;
  title: string;
  detail: string;
  /** Offer the retry of the first publish (`SC-S300`). */
  retry?: boolean;
}

const TABS: BnTab[] = [
  { id: "overview", label: "Overview" },
  { id: "tasks", label: "Tasks" },
  { id: "tail", label: "Live audit tail" },
  { id: "settings", label: "Settings" },
  { id: "danger", label: "Danger zone" },
];

const TAIL_COLUMNS: BnColumn[] = [
  { key: "at", label: "At", width: "1px", mono: true },
  { key: "subject", label: "Subject" },
  { key: "decision", label: "Decision", width: "1px" },
  { key: "tables", label: "Tables", mono: true },
  { key: "policies", label: "Applied policies" },
  { key: "rows", label: "Rows", align: "right", width: "1px", mono: true },
  { key: "ms", label: "ms", align: "right", width: "1px", mono: true },
];

const TASK_COLUMNS: BnColumn[] = [
  { key: "slot", label: "Slot", width: "1px", mono: true },
  { key: "id", label: "Task", mono: true },
  { key: "node", label: "Node", mono: true },
  { key: "state", label: "Observed", width: "1px" },
  { key: "desired", label: "Desired", width: "1px" },
  { key: "since", label: "Since", width: "1px" },
];

const JOB_COLUMNS: BnColumn[] = [
  { key: "type", label: "Operation", mono: true },
  { key: "status", label: "Status", width: "1px" },
  { key: "progress", label: "Progress" },
  { key: "actor", label: "Started by" },
  { key: "when", label: "When", width: "1px" },
];

/* ── Small formatters. Deliberately local to the page: the kit ships no date
      helper, and a shared one is not mine to add. ────────────────────────── */

/** Relative time, coarse on purpose — "4 minutes ago" is the question being
 * asked, "4m 12s" is not. Returns the empty string for an absent timestamp. */
function ago(iso: string | null | undefined): string {
  if (!iso) return "";
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "";
  const s = Math.round((Date.now() - then) / 1000);
  if (s < 0) return "just now";
  if (s < 45) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} hour${h === 1 ? "" : "s"} ago`;
  return `${Math.round(h / 24)} days ago`;
}

/** Wall-clock, for the tail where ordering within a second matters. */
function clock(iso: string): string {
  const t = new Date(iso);
  return Number.isNaN(t.getTime()) ? iso : t.toLocaleTimeString();
}

/**
 * page-instance renders one instance's console.
 *
 *   <page-instance .instanceId=${id}></page-instance>
 */
@customElement("page-instance")
export class PageInstance extends LitElement {
  /** Record id from the route. The bare 15-character id, not a prefixed one. */
  @property() instanceId = "";

  /* Record and its two observation sources. They fail independently: `SC-S317`
     requires the affected sub-panel to name the endpoint that failed while the
     rest of the page still renders. */
  @state() private instance: Instance | null = null;
  @state() private status: InstanceStatus | null = null;
  @state() private health: InstanceHealth | null = null;
  @state() private jobs: Job[] = [];
  @state() private newestGeneration: Generation | null = null;

  @state() private loading = true;
  /** The instance read itself failed. Nothing else can render. */
  @state() private loadError: ApiError | null = null;
  @state() private statusError: ApiError | null = null;
  @state() private healthError: ApiError | null = null;
  @state() private jobsError: ApiError | null = null;

  /** The caller's role on this instance's organization, once known. Undefined
   * means "not established" — the server is the authority either way, so an
   * unknown role never hides a control, it only stops us pre-disabling one. */
  @state() private orgRole: Role | undefined;

  @state() private tab = "overview";

  /* Live audit tail (`SC-F823`). `TailRecord` and not `AuditRecord`: an index row
     points at bytes in the raw tier that can be verified and a tail record points
     at nothing, so the two types are deliberately not interchangeable. */
  @state() private tail: TailRecord[] = [];
  @state() private tailProjection: AuditProjection | null = null;
  /** The sentence `SC-S813` requires this surface to carry, taken from the
   * response rather than restated here — copy in a UI file is copy a second
   * client can forget. */
  @state() private tailNotice = "";
  @state() private tailCapped = false;
  @state() private tailError: ApiError | null = null;
  @state() private tailLive = true;
  /** True once a tail response has actually arrived. Distinguishes "nothing has
   * been decided yet" from "we have not asked yet", which have opposite
   * remedies (`SC-S907`). */
  @state() private tailLoaded = false;
  private tailSeen = new Set<string>();

  /* Container log tail (`SC-F320`, v1). Loaded on demand. */
  @state() private logs = "";
  @state() private logsLoading = false;
  @state() private logsError: ApiError | null = null;

  /* Power operations (`SC-F317`). */
  @state() private confirm: "restart" | "suspend" | "resume" | "delete" | null = null;
  @state() private busy: "restart" | "suspend" | "resume" | "delete" | "retry" | null = null;
  @state() private actionError: ApiError | null = null;
  @state() private activeJob: Job | null = null;
  /** Typed-name confirmation for deletion (`SC-S318`). */
  @state() private deleteTyped = "";

  /* Step-up for deletion (`SC-F011`). `verifyFor` is the instance the dialog was
     opened for, so a grant is never spent on another one after a route change. */
  @state() private verifyOpen = false;
  @state() private verifyFor = "";
  @state() private otpId = "";
  @state() private otpCode = "";
  @state() private verifying = false;
  /** Null means "not looked up yet", which keeps the passkey branch visible. */
  @state() private hasPasskeys: boolean | null = null;

  private statusTimer = 0;
  private tailTimer = 0;
  private abort = new AbortController();
  private readonly onHashChange = () => this.syncTabFromHash();

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }

    header.page {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-start;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
    }
    .identity {
      flex: 1 1 20rem;
      min-width: 0;
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
      overflow-wrap: anywhere;
    }
    .sub {
      margin: var(--bino-space-xs) 0 0;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-sm);
      align-items: baseline;
    }
    .sub a,
    a.inline {
      color: var(--bino-primary);
    }
    .sub code,
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
    .badges {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--bino-space-sm);
    }

    .stack > * + * {
      margin-top: var(--bino-space-md);
    }
    .grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr));
      gap: var(--bino-space-md);
    }
    .row {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--bino-space-sm);
    }
    p {
      margin: 0 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-md);
      line-height: 1.5;
      max-width: 78ch;
    }
    p:last-child {
      margin-bottom: 0;
    }
    .muted {
      color: var(--bino-text-secondary);
      font-size: var(--bino-font-size-base);
    }
    h2 {
      margin: 0 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-md);
      font-weight: 600;
    }
    ul {
      margin: 0 0 var(--bino-space-sm);
      padding-left: var(--bino-space-lg);
      font-size: var(--bino-font-size-md);
      line-height: 1.5;
    }
    li + li {
      margin-top: var(--bino-space-xs);
    }

    /* The desired/observed pair. Two columns that are read against each other,
       which is why they sit in one surface rather than in two cards. */
    .pair {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(16rem, 1fr));
      gap: var(--bino-space-md);
    }
    .pair-col > h3 {
      margin: 0 0 var(--bino-space-sm);
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      font-weight: 600;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--bino-text-secondary);
    }

    .danger-card {
      border: 1px solid var(--bino-error-border);
      border-radius: var(--bino-radius-lg);
      background: var(--bino-surface);
      padding: var(--bino-space-lg);
    }
    .danger-card h2 {
      color: var(--bino-error);
    }
    .verify-branch {
      margin-bottom: var(--bino-space-sm);
    }
    .verify-or {
      margin: 0 0 var(--bino-space-sm);
      text-align: center;
      color: var(--bino-text-secondary);
      font-size: var(--bino-font-size-sm);
    }

    /* bn-code sizes to its host, so the host needs a height. */
    .log {
      display: block;
      height: 22rem;
    }
    .log > bn-code {
      display: block;
      height: 100%;
    }
  `;

  /* ── lifecycle ─────────────────────────────────────────────────────────── */

  override connectedCallback() {
    super.connectedCallback();
    this.syncTabFromHash();
    window.addEventListener("hashchange", this.onHashChange);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("hashchange", this.onHashChange);
    this.stopTimers();
    this.abort.abort();
  }

  override willUpdate(changed: Map<string, unknown>) {
    if (changed.has("instanceId")) {
      this.stopTimers();
      this.abort.abort();
      this.abort = new AbortController();
      this.resetForNewInstance();
      void this.load();
    }
  }

  private resetForNewInstance() {
    this.instance = null;
    this.status = null;
    this.health = null;
    this.jobs = [];
    this.newestGeneration = null;
    this.loading = true;
    this.loadError = null;
    this.statusError = null;
    this.healthError = null;
    this.jobsError = null;
    this.tail = [];
    this.tailSeen = new Set();
    this.tailNotice = "";
    this.tailCapped = false;
    this.tailLoaded = false;
    this.tailLive = true;
    this.tailError = null;
    this.logs = "";
    this.logsError = null;
    this.activeJob = null;
    this.actionError = null;
    this.verifyOpen = false;
    this.verifyFor = "";
    this.otpId = "";
    this.otpCode = "";
  }

  private stopTimers() {
    if (this.statusTimer) window.clearInterval(this.statusTimer);
    if (this.tailTimer) window.clearInterval(this.tailTimer);
    this.statusTimer = 0;
    this.tailTimer = 0;
  }

  private syncTabFromHash() {
    const id = location.hash.replace(/^#/, "");
    this.tab = TABS.some((t) => t.id === id) ? id : "overview";
    if (this.tab === "tail") this.startTail();
  }

  private selectTab(id: string) {
    this.tab = id;
    history.replaceState({}, "", `${location.pathname}${location.search}#${id}`);
    if (id === "tail") this.startTail();
    else this.stopTailTimer();
  }

  /* ── loading ───────────────────────────────────────────────────────────── */

  private async load() {
    if (!this.instanceId) return;
    this.loading = true;
    try {
      this.instance = await getInstance(this.instanceId);
      this.loadError = null;
    } catch (err) {
      this.loadError = parseError(err);
      this.loading = false;
      return;
    }
    this.loading = false;

    // Everything below is best-effort and independently fallible.
    void this.loadRole();
    void this.refreshObservations();
    void this.refreshJobs();
    this.statusTimer = window.setInterval(() => {
      void this.refreshObservations();
      // The retry button depends on the create job being over, and nothing
      // else refreshes the job list while the page is only watched.
      if (this.neverLive) void this.refreshJobs();
    }, STATUS_POLL_MS);
  }

  /** The caller's role decides which power operations are offered. A failure
   * here leaves the role undefined, which pre-disables nothing — the server
   * re-checks every route and answers `forbidden` with the required role. */
  private async loadRole() {
    const org = this.instance?.org;
    if (!org) return;
    try {
      const identity = await me();
      this.orgRole = identity.organizations.find((o) => o.id === org)?.role;
    } catch {
      this.orgRole = undefined;
    }
  }

  private async refreshObservations() {
    await Promise.all([this.refreshStatus(), this.refreshHealth()]);
  }

  private async refreshStatus() {
    try {
      this.status = await getInstanceStatus(this.instanceId);
      this.statusError = null;
    } catch (err) {
      this.statusError = parseError(err);
    }
  }

  private async refreshHealth() {
    try {
      this.health = await getInstanceHealth(this.instanceId);
      this.healthError = null;
    } catch (err) {
      this.healthError = parseError(err);
    }
  }

  private async refreshJobs() {
    try {
      const page = await listInstanceJobs(this.instanceId, { limit: 8 });
      this.jobs = page.items;
      this.jobsError = null;
    } catch (err) {
      this.jobsError = parseError(err);
    }
    // Best-effort: a caller without the policies feature gets 403 and no row.
    // The element is reused across instances, so a late answer is dropped.
    const id = this.instanceId;
    let newest: Generation | null = null;
    try {
      // A draft is not a publish attempt, so it must not hide a failed one.
      newest =
        (await listGenerations(id)).items.find((g) => g.state !== "draft" && g.state !== "validated") ?? null;
    } catch {
      // Keep what is shown: one failed read must not hide a failure.
      newest = this.newestGeneration;
    }
    if (id === this.instanceId) this.newestGeneration = newest;
  }

  /** The newest finished lock-holding job, only when it failed. A later
   * success hides it. */
  private get lastFailedJob(): Job | null {
    const j = this.jobs.find((x) => isTerminal(x.status) && holdsLock(x.type));
    return j && (j.status === "failed" || j.status === "timed_out") && j.error ? j : null;
  }

  /* ── the live audit tail (`SC-F823`) ───────────────────────────────────── */

  private startTail() {
    if (this.tailTimer || !this.tailLive) return;
    void this.tickTail();
    this.tailTimer = window.setInterval(() => void this.tickTail(), TAIL_POLL_MS);
  }

  /** Stops the poll without claiming the tail was paused by the reader — that
   * is what leaving the tab does. */
  private stopTailTimer() {
    if (this.tailTimer) window.clearInterval(this.tailTimer);
    this.tailTimer = 0;
  }

  private pauseTail() {
    this.tailLive = false;
    this.stopTailTimer();
  }

  private resumeTail() {
    this.tailError = null;
    this.tailLive = true;
    this.startTail();
  }

  private async tickTail() {
    if (this.tab !== "tail") return;
    try {
      const res = await getAuditTail(this.instanceId, { n: TAIL_N });
      this.tailProjection = res.projection;
      this.tailNotice = res.notice;
      this.tailCapped = res.capped;
      this.mergeTail(res.items);
      this.tailLoaded = true;
      this.tailError = null;
    } catch (err) {
      // Stop rather than hammer a failing instance every three seconds. The
      // reader restarts it, which is also the honest affordance: a tail that
      // silently retries looks live when it is not.
      this.tailError = parseError(err);
      this.pauseTail();
    }
  }

  /**
   * De-duplicates on the record hash, newest first, capped at the endpoint's own
   * limit (`SC-F823`).
   *
   * A tail record carries no chain sequence — it points at no bytes — so the sort
   * is on the instance's own timestamp. Ordering within one millisecond is
   * therefore arrival order, which is honest: the endpoint states no total order
   * and inventing one from a re-marshalled struct would be a claim about a chain
   * this view cannot make.
   */
  private mergeTail(items: TailRecord[]) {
    const fresh = items.filter((r) => !this.tailSeen.has(r.hash));
    if (fresh.length === 0) return;
    fresh.sort((a, b) => Date.parse(b.ts) - Date.parse(a.ts));
    const next = [...fresh, ...this.tail].slice(0, TAIL_CAP);
    this.tail = next;
    this.tailSeen = new Set(next.map((r) => r.hash));
  }

  /* ── container logs (`SC-F320`, v1) ────────────────────────────────────── */

  private async loadLogs() {
    this.logsLoading = true;
    this.logsError = null;
    try {
      this.logs = await getInstanceLogs(this.instanceId, { tail: 200 });
    } catch (err) {
      this.logsError = parseError(err);
    } finally {
      this.logsLoading = false;
    }
  }

  /* ── power operations (`SC-F317`, `SC-F318`, `SC-F332`) ────────────────── */

  private async runPower(
    kind: "restart" | "suspend" | "resume" | "delete" | "retry",
    verification?: string,
  ) {
    // The page element is reused across instances, so an answer can arrive
    // after the route moved on.
    const id = this.instanceId;
    this.confirm = null;
    this.busy = kind;
    this.actionError = null;
    const label = POWER_LABELS[kind];
    try {
      const started =
        kind === "restart"
          ? await restartInstance(id)
          : kind === "suspend"
            ? await suspendInstance(id)
            : kind === "resume"
              ? await resumeInstance(id)
              : kind === "retry"
                ? await retryInstance(id)
                : await deleteInstance(id, { verification });

      this.activeJob = started.job;
      void this.refreshJobs();

      // One progress mechanism for every long operation — `SC-F332`/`I7`. If a
      // second poll loop ever appears in this file, it is a bug.
      const done = await watchJob(started.job.id, {
        onUpdate: (job) => {
          this.activeJob = job;
        },
        signal: this.abort.signal,
      });
      const failure = jobFailure(done);
      if (failure) {
        this.actionError = failure;
        toastPersistent(failure.message, "error", `${label} failed`);
      } else {
        toast(POWER_DONE[kind], "success");
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      if (id !== this.instanceId) return;
      const e = parseError(err);
      if (
        kind === "delete" &&
        (e.code === "verification_required" || e.code === "verification_invalid")
      ) {
        // The grant was wrong or expired, so say why the dialog is back.
        if (e.code === "verification_invalid") toast(e.message, "error");
        this.openVerify();
        return;
      }
      this.actionError = e;
      toastPersistent(e.message, "error", `${label} failed`);
    } finally {
      this.busy = null;
      this.deleteTyped = "";
      void this.refreshObservations();
      void this.refreshJobs();
    }
  }

  /* ── step-up for deletion (`SC-F011`) ──────────────────────────────────── */

  private openVerify() {
    this.verifyFor = this.instanceId;
    this.otpId = "";
    this.otpCode = "";
    this.verifyOpen = true;
    void this.ensurePasskeyInfo();
  }

  private closeVerify() {
    this.verifyOpen = false;
  }

  /** Runs the delete with a fresh grant. A dialog that was cancelled or reset
   * while the verify call was in flight must not delete. */
  private resumeDelete(grant: string) {
    if (!this.verifyOpen || this.verifyFor !== this.instanceId) return;
    this.verifyOpen = false;
    void this.runPower("delete", grant);
  }

  private async ensurePasskeyInfo() {
    if (this.hasPasskeys !== null) return;
    if (!webauthnSupported()) {
      this.hasPasskeys = false;
      return;
    }
    try {
      this.hasPasskeys = (await listPasskeys()).length > 0;
    } catch {
      // Deliberately left null; see the field's doc comment.
    }
  }

  private async verifyByPasskey() {
    if (this.verifying) return;
    this.verifying = true;
    try {
      await this.ensurePasskeyInfo();
      const grant = await verifyWithPasskey({
        action: "delete_instance",
        target: this.verifyFor,
      });
      this.resumeDelete(grant);
    } catch (err) {
      // A dismissed authenticator prompt is a cancellation, not a failure: the
      // dialog stays open, nothing is said, and the email branch is still there.
      const message = passkeyFailureMessage(err);
      if (message) toast(message, "error");
    } finally {
      this.verifying = false;
    }
  }

  private async sendCode() {
    if (this.verifying) return;
    this.verifying = true;
    try {
      const { otpId } = await requestStepUpOtp();
      this.otpId = otpId;
      this.otpCode = "";
    } catch (err) {
      toast(parseError(err).message, "error");
    } finally {
      this.verifying = false;
    }
  }

  private async confirmCode() {
    const code = this.otpCode.trim();
    if (code === "" || this.otpId === "" || this.verifying) return;
    this.verifying = true;
    try {
      const grant = await confirmStepUpOtp(this.otpId, code, {
        action: "delete_instance",
        target: this.verifyFor,
      });
      this.resumeDelete(grant);
    } catch (err) {
      // The server answers one uniform `otp_invalid` for wrong, expired and
      // foreign codes alike, so there is exactly one failure path to render.
      toast(parseError(err).message, "error");
    } finally {
      this.verifying = false;
    }
  }

  /* ── derived reads ─────────────────────────────────────────────────────── */

  /** The lifecycle state to render. The status endpoint's observation wins over
   * the record's summary field when both are present, because the status route
   * is the one that polled the cluster. */
  private get lifecycle(): InstanceState {
    return this.status?.observed.state ?? this.instance?.observed_state ?? "draft";
  }

  /** The observation time when the server calls it stale (`SC-S317`). The
   * server owns the rule: it knows how often the reconciler observes an
   * instance in each state, which this page's own poll says nothing about. */
  private get staleSince(): string | null {
    const o = this.status?.observed;
    return o?.stale && o.observed_at ? o.observed_at : null;
  }

  /**
   * Everything that outranks the state name. The k3s backend reports no paused
   * or rolling-back rollout, so there is no banner for one.
   */
  private get alerts(): Alert[] {
    const out: Alert[] = [];
    const s = this.status;
    if (!s) return out;

    if (s.reconcile.quarantined) {
      out.push({
        variant: "error",
        badge: "Quarantined",
        title: "This instance is quarantined",
        detail:
          "The reconciler observed the instance's Service without a matching desired-state record and deleted its Pod rather than removing anything else. The Service, the Secrets and the data directory are kept. Contact support before changing anything.",
      });
    }

    // The reconciler's own verdict, and the one banner that reports a
    // CONTROL-PLANE condition rather than an instance one. `blocked` means
    // automatic repair has stopped — the failure budget of `02` §9.6 is spent,
    // or an operator paused this instance — so nothing changes until a person
    // acts, and a page that showed only "provisioning" would be waiting for
    // something that is not coming. The substrate's message is rendered
    // verbatim (`02` §9.4 D11): summarizing it loses the constraint that
    // failed, which is the only part anybody can act on.
    const retryable = this.retryable;
    if (s.reconcile.state === "blocked" || s.reconcile.error) {
      const blocked = s.reconcile.state === "blocked";
      const failures = s.reconcile.consecutive_failures ?? 0;
      out.push({
        variant: blocked ? "error" : "warning",
        badge: blocked ? "Repair stopped" : "Repair failing",
        title: blocked
          ? "Automatic repair has stopped for this instance"
          : "The last reconcile pass could not repair this instance",
        detail: [
          s.reconcile.error?.message,
          s.reconcile.error ? `Reported as ${s.reconcile.error.code}.` : undefined,
          failures > 0 ? `${failures} consecutive failure${failures === 1 ? "" : "s"}.` : undefined,
          blocked
            ? "Nothing further is attempted automatically."
            : s.reconcile.next_attempt
              ? `Next attempt ${ago(s.reconcile.next_attempt)}.`
              : undefined,
        ]
          .filter(Boolean)
          .join(" "),
        retry: retryable,
      });
    } else if (retryable && this.lifecycle === "failed") {
      out.push({
        variant: "error",
        badge: "Not running",
        title: "The first publish did not finish",
        detail: "Retry publishes the first generation again.",
        retry: true,
      });
    }

    if (!s.drift.digest_match) {
      out.push({
        variant: "warning",
        badge: "Digest drift",
        title: "The running config digest is not the live generation's digest",
        detail:
          "The instance is serving a configuration that is not the one recorded as live. Publishing again re-materializes the bundle and reloads it.",
      });
    }

    if (
      s.desired.generation !== null &&
      s.observed.generation !== null &&
      s.desired.generation !== s.observed.generation
    ) {
      out.push({
        variant: "warning",
        badge: "Generation lag",
        title: `Generation ${s.observed.generation} is running; generation ${s.desired.generation} is desired`,
        detail:
          "A publish has been recorded that the cluster has not converged to. If this does not clear within a few minutes, the rollout is stuck rather than slow.",
      });
    }

    const { desired, running } = s.observed.replicas;
    if (running < desired && this.lifecycle !== "suspended") {
      out.push({
        variant: "warning",
        badge: `${running}/${desired} replicas`,
        title: "Fewer tasks are running than desired",
        detail:
          "One replica per instance is an invariant, not a default — a Sluice process owns its audit chain and its approval store exclusively — so a missing replica means no service, not reduced capacity.",
      });
    }

    const stale = this.staleSince;
    if (stale) {
      out.push({
        variant: "warning",
        badge: "Observation stale",
        title: "This is not a current reading",
        detail: `The cluster was last observed ${ago(stale)}. Everything below is that observation, not a live one.`,
      });
    }

    if (this.health && !this.health.live) {
      out.push({
        variant: "info",
        badge: "Not checked live",
        title: "The instance was not contacted for this view",
        detail:
          "The health below is the last stored observation; the notes say why no live probe ran. The lifecycle state above still reflects what Kubernetes reports for the Pod.",
      });
    } else if (this.health && !this.health.reachable) {
      out.push({
        variant: "warning",
        badge: "Unreachable",
        title: "The instance did not answer",
        detail:
          "Readiness and datasource health could not be read; the notes below say what was tried. The lifecycle state above still reflects what Kubernetes reports for the Pod.",
      });
    }

    return out;
  }

  /** An instance that never went live and is in a state the retry route takes. */
  private get neverLive(): boolean {
    const state = this.lifecycle;
    return this.instance?.generation === null && (state === "provisioning" || state === "failed");
  }

  /** Whether the retry of the first publish is offered. An open job that holds
   * the instance lock means the route would answer 409, so the button waits. */
  private get retryable(): boolean {
    if (!this.canOwn || !this.neverLive) return false;
    if (this.activeJob && !isTerminal(this.activeJob.status)) return false;
    return !this.jobs.some((j) => !isTerminal(j.status) && holdsLock(j.type));
  }

  private get canRestart(): boolean {
    return this.orgRole === undefined || atLeast(this.orgRole, "maintainer");
  }

  private get canOwn(): boolean {
    return this.orgRole === undefined || atLeast(this.orgRole, "owner");
  }

  /* ── shared render helpers ─────────────────────────────────────────────── */

  /**
   * The failure state every panel in this page uses. It renders the `apierr`
   * code and message and exposes the request id, because that is the string a
   * reader quotes in a support ticket (`OD-11`, `SC-S907`).
   */
  private renderError(heading: string, err: ApiError, retry?: () => void): TemplateResult {
    return html`
      <bn-card heading=${heading}>
        ${retry
          ? html`<bn-button slot="actions" size="sm" @click=${retry}>Try again</bn-button>`
          : nothing}
        <div role="alert">
          <p>${err.message}</p>
          <bn-kv
            .rows=${[
              { label: "Error code", value: err.code },
              { label: "Request id", value: err.requestId || undefined },
            ] as BnKvRow[]}
            unsetText="never reached the server"
          ></bn-kv>
          <p class="muted">
            ${err.requestId
              ? "Quote the request id in a support ticket and we can find this exact request."
              : "This request did not reach Sluice Cloud, so there is no reference to quote. Check the connection and try again."}
          </p>
        </div>
      </bn-card>
    `;
  }

  private stateBadge(): TemplateResult {
    const facts = STATES[this.lifecycle];
    return html`<bn-badge variant=${facts.variant} ?dot=${facts.dot}>${facts.label}</bn-badge>`;
  }

  /* ── header ────────────────────────────────────────────────────────────── */

  private renderHeader(inst: Instance): TemplateResult {
    const alerts = this.alerts;
    return html`
      <header class="page">
        <div class="identity">
          <h1>${inst.display_name || inst.name}</h1>
          <p class="sub">
            <a href=${`https://${inst.hostname}`} rel="noreferrer noopener" target="_blank"
              ><code>${inst.hostname}</code></a
            >
            <span>·</span>
            <span>${inst.size}</span>
            <span>·</span>
            <span>Sluice ${inst.sluice_version}</span>
            <span>·</span>
            <span
              >${inst.generation === null
                ? "never published"
                : `generation ${inst.generation} live`}</span
            >
          </p>
        </div>
        <div class="badges">
          ${this.stateBadge()}
          ${alerts.map((a) => html`<bn-badge variant=${a.variant}>${a.badge}</bn-badge>`)}
        </div>
      </header>
    `;
  }

  /* ── overview tab (`SC-F319`) ──────────────────────────────────────────── */

  private renderOverview(inst: Instance): TemplateResult {
    const alerts = this.alerts;
    const facts = STATES[this.lifecycle];
    const s = this.status;

    return html`
      <div class="stack">
        ${alerts.map(
          (a) => html`
            <bn-panel eyebrow=${a.badge}>
              <h2>${a.title}</h2>
              <p>${a.detail}</p>
              ${a.retry
                ? html`<bn-button
                    size="sm"
                    ?disabled=${this.busy !== null}
                    @click=${() => void this.runPower("retry")}
                    >Retry</bn-button
                  >`
                : nothing}
            </bn-panel>
          `,
        )}

        <bn-card heading="Lifecycle state">
          <div class="row" style="margin-bottom:var(--bino-space-sm)">
            ${this.stateBadge()}
            <span class="muted"
              >${this.staleSince
                ? `observed ${ago(this.staleSince)}`
                : s
                  ? `observed ${ago(s.drift.checked) || "just now"}`
                  : ""}</span
            >
          </div>
          <p>${facts.meaning}</p>
          ${this.lifecycle === "healthy"
            ? html`<p class="muted">
                Healthy is fused from task state, health, readiness and the config digest. None of
                those observes the audit sink: an instance whose state volume is full reads healthy
                here and refuses every query with an audit-unavailable error. That gap is closed by
                volume alarming, not by this panel, and it is stated rather than papered over.
              </p>`
            : nothing}
          ${this.lifecycle === "deleting" ? this.renderReclamation() : nothing}
        </bn-card>

        ${this.statusError
          ? this.renderError("Cluster observation unavailable — GET /v1/instances/{id}/status", this.statusError, () =>
              void this.refreshStatus(),
            )
          : s
            ? this.renderDesiredObserved(s)
            : html`<bn-card heading="Desired versus observed"
                ><bn-spinner block label="Reading the cluster"></bn-spinner
              ></bn-card>`}

        ${this.renderHealth()} ${this.renderOperations(inst)}
      </div>
    `;
  }

  private renderDesiredObserved(s: InstanceStatus): TemplateResult {
    const desired: BnKvRow[] = [
      { label: "Desired state", value: s.desired.state },
      {
        label: "Generation",
        value: s.desired.generation === null ? undefined : String(s.desired.generation),
      },
      { label: "Pinned image digest", value: s.desired.image_digest },
      { label: "Size class", value: s.desired.size },
      { label: "Pinned node", value: s.desired.pinned_node || undefined },
    ];
    // Spread only when present: the kv's unset text is about reconcile passes.
    const failed = this.lastFailedJob;
    const gen = this.newestGeneration;
    const failures: BnKvRow[] = [];
    if (failed?.error) {
      failures.push({
        label: "Last failed job",
        value: `${failed.type} ${failed.status}${failed.finished ? ` ${ago(failed.finished)}` : ""}: ${failed.error.code}: ${failed.error.message}`,
      });
    }
    if (gen?.failure_reason) {
      failures.push({
        label: "Last publish failure",
        value: `Generation ${gen.generation}: ${gen.failure_reason}`,
      });
    }
    const observed: BnKvRow[] = [
      { label: "Observed state", value: STATES[s.observed.state].label },
      {
        label: "Generation",
        value: s.observed.generation === null ? undefined : String(s.observed.generation),
      },
      { label: "Live config digest", value: s.observed.config_digest ?? undefined },
      {
        label: "Replicas",
        value: `${s.observed.replicas.running} running / ${s.observed.replicas.desired} desired`,
      },
    ];

    return html`
      <bn-card heading="Desired versus observed">
        <p class="muted">
          The instance record is the desired state and the cluster is a projection of it. These two
          columns are never fused: when they disagree, that disagreement is the answer.
        </p>
        <div class="pair" style="margin-top:var(--bino-space-md)">
          <div class="pair-col">
            <h3>Desired — what was asked for</h3>
            <bn-kv .rows=${desired}></bn-kv>
          </div>
          <div class="pair-col">
            <h3>Observed — what the cluster reports</h3>
            <bn-kv .rows=${observed}></bn-kv>
          </div>
        </div>

        <div style="margin-top:var(--bino-space-lg)">
          <h2>Rollout</h2>
          ${s.observed.update_status
            ? html`<bn-kv
                .rows=${[
                  { label: "Update state", value: s.observed.update_status.state },
                  { label: "Message", value: s.observed.update_status.message || undefined },
                  {
                    label: "Completed",
                    value: s.observed.update_status.completed ?? undefined,
                  },
                ] as BnKvRow[]}
                unsetText="—"
              ></bn-kv>`
            : html`<p class="muted">
                No rollout is reported for this instance's Pod. Nothing is rolling out.
              </p>`}
        </div>

        <div style="margin-top:var(--bino-space-lg)">
          <h2>Reconciliation</h2>
          <bn-kv
            .rows=${[
              { label: "Digest match", value: s.drift.digest_match ? "yes" : "no" },
              { label: "Drift checked", value: `${s.drift.checked} (${ago(s.drift.checked)})` },
              {
                label: "Last reconcile pass",
                value: s.reconcile.last_pass
                  ? `${s.reconcile.last_pass} (${ago(s.reconcile.last_pass)})`
                  : undefined,
              },
              { label: "Quarantined", value: s.reconcile.quarantined ? "yes" : "no" },
              { label: "Reconcile state", value: s.reconcile.state || undefined },
              { label: "Last reconcile error", value: s.reconcile.error?.message },
              ...failures,
              {
                label: "Consecutive failures",
                value: s.reconcile.consecutive_failures
                  ? String(s.reconcile.consecutive_failures)
                  : undefined,
              },
            ] as BnKvRow[]}
            unsetText="no pass recorded yet"
          ></bn-kv>
        </div>
      </bn-card>
    `;
  }

  /** `SC-F916` / `SC-S323`: deletion is not complete until a node-local agent
   * confirms every volume. Reporting success ahead of that would be a
   * compliance failure, not a cosmetic bug. */
  private renderReclamation(): TemplateResult {
    const r = this.status?.reclamation;
    if (!r) {
      return html`<p class="muted">
        Volume reclamation has not been reported yet. The record stays in deleting until every
        volume on the pinned node is confirmed reclaimed — a removed service is not erased data.
      </p>`;
    }
    const rows: BnKvRow[] = r.volumes.map((v) => ({
      label: v.volume,
      value:
        v.state === "reclaimed"
          ? `reclaimed ${v.confirmed ? ago(v.confirmed) : ""}`.trim()
          : `${v.state}${v.reason ? ` — ${v.reason}` : ""}`,
      copyable: false,
    }));
    const outstanding = r.volumes.filter((v) => v.state !== "reclaimed").length;
    return html`
      <div style="margin-top:var(--bino-space-md)">
        <h2>Volume reclamation</h2>
        <p>
          ${outstanding === 0
            ? "Every volume was confirmed reclaimed. The record advances to deleted on the next pass."
            : `${outstanding} volume${outstanding === 1 ? " has" : "s have"} not been reclaimed yet. The data has not been erased.`}
          The volumes are node-local, so only the reaper on
          <code>${r.node}</code> can remove them; that node is
          ${r.node_reachable ? "reachable" : "not reachable"}.
        </p>
        <bn-kv .rows=${rows}></bn-kv>
      </div>
    `;
  }

  private renderHealth(): TemplateResult {
    if (this.healthError) {
      return this.renderError(
        "Instance health unavailable — GET /v1/instances/{id}/health",
        this.healthError,
        () => void this.refreshHealth(),
      );
    }
    const h = this.health;
    if (!h) {
      return html`<bn-card heading="Health and datasources"
        ><bn-spinner block label="Reading instance health"></bn-spinner
      ></bn-card>`;
    }

    const rows: BnKvRow[] = [
      { label: "Reachable", value: h.reachable ? "yes" : "no" },
      { label: "Health", value: h.rest.health },
      { label: "Ready", value: h.rest.ready ? "yes" : "no (503)" },
      { label: "Checked", value: `${h.rest.checked} (${ago(h.rest.checked)})` },
      { label: "Config digest", value: h.admin?.config_digest },
      {
        label: "Config version",
        value: h.admin ? String(h.admin.config_version) : undefined,
      },
      { label: "Sluice build", value: h.admin?.version },
    ];

    const dsColumns: BnColumn[] = [
      { key: "name", label: "Datasource", mono: true },
      { key: "type", label: "Type", width: "1px" },
      { key: "healthy", label: "State", width: "1px" },
      { key: "latency", label: "Latency", align: "right", width: "1px", mono: true },
      { key: "checked", label: "Checked", width: "1px" },
      { key: "error", label: "Error" },
    ];
    const dsRows: BnRow[] = h.datasources.map((d) => ({
      name: d.name,
      type: d.type,
      healthy: html`<bn-badge variant=${d.healthy ? "success" : "error"} dot
        >${d.healthy ? "healthy" : "unhealthy"}</bn-badge
      >`,
      latency: d.latency_ms === null ? "—" : `${d.latency_ms} ms`,
      checked: ago(d.last_check),
      error: d.error ?? "",
    }));

    return html`
      <bn-card heading="Health and datasources">
        <bn-kv .rows=${rows} unsetText="not reported"></bn-kv>
        ${h.notes.length
          ? html`<ul style="margin-top:var(--bino-space-md)">
              ${h.notes.map((n) => html`<li>${n}</li>`)}
            </ul>`
          : nothing}
        <div style="margin-top:var(--bino-space-md)">
          <bn-table
            .columns=${dsColumns}
            .rows=${dsRows}
            caption="Datasource health"
            empty="No datasource has been probed yet."
          >
            <bn-empty-state
              slot="empty"
              icon="◇"
              heading=${h.reachable ? "No datasources configured" : "Not probed yet"}
              message=${h.reachable
                ? "Add a datasource and publish; health appears here after the first probe sweep."
                : "Readiness is fail-closed at boot, so datasource health stays empty until the instance answers and the first probe sweep runs."}
            ></bn-empty-state>
          </bn-table>
        </div>
      </bn-card>
    `;
  }

  /** `SC-F332`: one job model for every long operation, so this is one panel and
   * not five progress widgets. */
  private renderOperations(inst: Instance): TemplateResult {
    if (this.jobsError) {
      return this.renderError("Operations unavailable", this.jobsError, () => void this.refreshJobs());
    }
    const live = this.activeJob && !isTerminal(this.activeJob.status) ? this.activeJob : null;
    const rows: BnRow[] = this.jobs.map((j) => ({
      id: j.id,
      type: j.type,
      status: html`<bn-badge variant=${jobVariant(j.status)}>${j.status}</bn-badge>`,
      progress: j.progress ? `${j.progress.step} (${j.progress.index}/${j.progress.total})` : "—",
      actor: j.actor.account ?? j.actor.key_id ?? j.actor.kind,
      when: ago(j.finished ?? j.started ?? j.created),
    }));

    return html`
      <bn-card heading="Operations">
        <bn-button slot="actions" size="sm" @click=${() => void this.refreshJobs()}>Refresh</bn-button>
        ${live
          ? html`<bn-panel eyebrow="In flight">
              <div class="row">
                <bn-spinner></bn-spinner>
                <span
                  >${live.type} —
                  ${live.progress
                    ? `${live.progress.step} (${live.progress.index} of ${live.progress.total})`
                    : live.status}</span
                >
                <a class="inline" href=${hrefFor("jobs", live.id)}>Open job</a>
              </div>
            </bn-panel>`
          : nothing}
        <div style="margin-top:${live ? "var(--bino-space-md)" : "0"}">
          <bn-table
            .columns=${JOB_COLUMNS}
            .rows=${rows}
            caption="Recent operations on this instance"
            empty="No operation has run on this instance yet."
          >
            <bn-empty-state
              slot="empty"
              icon="⟲"
              heading="No operations yet"
              message=${inst.generation === null
                ? "Provisioning, publishing, restarts and deletion all appear here as jobs. Nothing has been published yet."
                : "Provisioning, publishing, restarts and deletion all appear here as jobs."}
            ></bn-empty-state>
          </bn-table>
        </div>
        <p class="muted" style="margin-top:var(--bino-space-md)">
          Every operation that cannot finish inside one request is the same job object, whatever
          started it. Full history is on the
          <a class="inline" href=${hrefFor("instances", this.instanceId, "jobs")}>jobs tab</a>.
        </p>
      </bn-card>
    `;
  }

  /* ── tasks tab (`SC-F319`, `SC-F320`) ──────────────────────────────────── */

  private renderTasks(): TemplateResult {
    if (this.statusError) {
      return this.renderError(
        "Task view unavailable — GET /v1/instances/{id}/status",
        this.statusError,
        () => void this.refreshStatus(),
      );
    }
    const s = this.status;
    if (!s) {
      return html`<bn-card heading="Tasks"
        ><bn-spinner block label="Reading tasks"></bn-spinner
      ></bn-card>`;
    }

    const rows: BnRow[] = s.observed.tasks.map((t) => ({
      id: t.id,
      slot: String(t.slot),
      node: t.node,
      state: html`<bn-badge variant=${taskVariant(t.state)}>${t.state}</bn-badge>`,
      desired: t.desired_state,
      since: ago(t.since),
    }));

    return html`
      <div class="stack">
        <bn-card heading="Tasks">
          <p class="muted">
            One replica per instance is an invariant, not a default: a Sluice process owns its
            hash-chained audit file and its approval and budget stores exclusively, and two writers
            is corruption. No surface in this product offers a replica count. More than one row here
            is a rollout in progress, not capacity.
          </p>
          <div style="margin-top:var(--bino-space-md)">
            <bn-table
              .columns=${TASK_COLUMNS}
              .rows=${rows}
              caption="The instance's Pod, as the kubelet reports it"
              empty="No task is placed."
            >
              <bn-empty-state
                slot="empty"
                icon="□"
                heading=${this.lifecycle === "suspended"
                  ? "Suspended — no task by intent"
                  : "No task is placed"}
                message=${this.lifecycle === "suspended"
                  ? "A suspended instance has no Pod. Resume it from the danger zone to start one again."
                  : "Kubernetes reports no Pod for this instance. If the instance is not draft or suspended, the Pod is either waiting to be scheduled or does not exist."}
              ></bn-empty-state>
            </bn-table>
          </div>
        </bn-card>

        <bn-card heading="Container log tail">
          <bn-button
            slot="actions"
            size="sm"
            ?loading=${this.logsLoading}
            ?disabled=${!this.canRestart}
            @click=${() => void this.loadLogs()}
            >${this.logs ? "Reload" : "Load last 200 lines"}</bn-button
          >
          <p class="muted">
            A bounded, credential-stripped tail of the container's own log, not a live stream and not
            the audit trail. It reads whatever the node's logging driver retained, so it works only
            with the local, json-file and journald drivers and is empty otherwise. Nothing here is
            evidence: the audit trail is the record.
          </p>
          ${!this.canRestart
            ? html`<p class="muted">Reading container logs needs the maintainer role.</p>`
            : nothing}
          ${this.logsError
            ? html`<div style="margin-top:var(--bino-space-md)">
                ${this.renderError("Log tail unavailable — GET /v1/instances/{id}/logs", this.logsError, () =>
                  void this.loadLogs(),
                )}
              </div>`
            : this.logs
              ? html`<div class="log" style="margin-top:var(--bino-space-md)">
                  <bn-code language="plain" label="Container log tail" .value=${this.logs}></bn-code>
                </div>`
              : this.logsLoading
                ? html`<bn-spinner block label="Reading the log tail"></bn-spinner>`
                : nothing}
        </bn-card>
      </div>
    `;
  }

  /* ── live audit tail tab (`SC-F823`) ───────────────────────────────────── */

  private renderTail(inst: Instance): TemplateResult {
    const minimized = inst.audit_profile === "minimized";
    const showSql = this.tail.some((r) => typeof r.sql_sample === "string");
    const columns = showSql
      ? [...TAIL_COLUMNS, { key: "sql", label: "SQL", mono: true } as BnColumn]
      : TAIL_COLUMNS;

    // Every analytic column on a tail record is optional, and for good reason: a
    // `genesis` row has no decision and only `query-result` carries rows. An em
    // dash says "this event type has no such field"; a zero would be a number the
    // instance never reported.
    const rows: BnRow[] = this.tail.map((r) => ({
      id: r.hash,
      at: clock(r.ts),
      subject: r.subject.sub ?? "—",
      decision: r.decision
        ? html`<bn-badge .variant=${decisionVariant(r.decision)}>${r.decision}</bn-badge>`
        : html`<bn-badge variant="neutral">${r.event_type}</bn-badge>`,
      tables: r.tables?.join(", ") || "—",
      policies: r.applied_policies?.join(", ") || "—",
      rows: r.rows === undefined ? "—" : String(r.rows),
      ms: r.duration_ms === undefined ? "—" : String(r.duration_ms),
      ...(showSql ? { sql: r.sql_sample ?? "" } : {}),
    }));

    return html`
      <div class="stack">
        <bn-panel eyebrow="Live tail">
          <p>
            <!-- The response's own notice, verbatim. The literal is the pre-first-response
                 fallback only: a tail that renders once without its lossiness warning is
                 the failure SC-S813 is about, so there is no state in which this
                 paragraph is empty. -->
            <strong
              >${this.tailNotice ||
              "Live tail — may skip records under high volume; the explorer is complete."}</strong
            >
          </p>
          ${this.tailCapped
            ? html`<p>
                <strong>The instance clamped this request.</strong> Fewer records were returned than
                were asked for, because the accessory's own cap is lower than the request — so what
                is below is the tail's maximum, not the instance's recent history.
              </p>`
            : nothing}
          <p>
            This reads the instance directly, so it has zero ingest lag: change a policy, run a
            query, see the decision. That is the only thing it is for. It returns the
            <strong>last ${TAIL_N} records only</strong>, is capped at ${TAIL_CAP} by the instance,
            takes <strong>no time range and no filter</strong>, and is lost across a restart. There
            is no search box here because the endpoint cannot answer one —
            <a class="inline" href=${hrefFor("instances", this.instanceId, "audit")}
              >the audit explorer</a
            >
            is the search surface, it is the ingested copy, and it is what is authoritative.
          </p>
          <p class="muted">
            The records below are re-marshalled from the decoded struct, not the canonical wire
            bytes the hash chain covers, so nothing here can be used for an export, a verification
            or a chain claim. Use the explorer for anything durable.
          </p>
        </bn-panel>

        ${minimized
          ? html`<bn-panel eyebrow="Audit profile">
              <p>
                This instance runs the <strong>Minimized</strong> privacy profile, so query text is
                not recorded at all and no SQL column appears here or anywhere else. That is the
                profile working, not a missing field.
              </p>
            </bn-panel>`
          : nothing}
        ${this.tailProjection?.applied
          ? html`<bn-panel eyebrow="Redacted view">
              <p>
                Some fields are withheld from this view${this.tailProjection.omitted?.length
                  ? html` — ${this.tailProjection.omitted.join(", ")}`
                  : nothing}.
                ${this.tailProjection.reason === "role_below_maintainer"
                  ? "They are shown to maintainers and above; an organization owner can change your role."
                  : "They are never shown to this credential class, whatever your role — use a session or a general key."}
              </p>
            </bn-panel>`
          : nothing}

        <bn-card heading="Decisions" flush>
          <div slot="actions" class="row">
            <span class="muted">${this.tail.length} held</span>
            ${this.tailLive
              ? html`<bn-button size="sm" @click=${() => this.pauseTail()}>Pause</bn-button>`
              : html`<bn-button size="sm" variant="primary" @click=${() => this.resumeTail()}
                  >Resume</bn-button
                >`}
          </div>
          ${this.tailError
            ? html`<div style="padding:var(--bino-space-lg)">
                ${this.renderError(
                  "Tail unavailable — GET /admin/audit/tail",
                  this.tailError,
                  () => this.resumeTail(),
                )}
              </div>`
            : html`
                <bn-table
                  .columns=${columns}
                  .rows=${rows}
                  ?loading=${this.tailLive && !this.tailLoaded}
                  caption="Live audit tail"
                >
                  <bn-empty-state
                    slot="empty"
                    icon="∿"
                    heading=${this.tailLoaded ? "Nothing has been decided yet" : "Tail not started"}
                    message=${this.tailLoaded
                      ? "The tail is connected and the instance has decided no query since it started. Run a query against the endpoint and it appears here within three seconds."
                      : "The tail is paused. Resume it to start polling the instance again."}
                  ></bn-empty-state>
                </bn-table>
              `}
        </bn-card>
      </div>
    `;
  }

  /* ── settings tab (`SC-F323`) ──────────────────────────────────────────── */

  private renderSettings(inst: Instance): TemplateResult {
    return html`
      <div class="stack">
        <bn-card heading="Identity">
          <bn-kv
            .rows=${[
              { label: "Name", value: inst.name },
              { label: "Display name", value: inst.display_name },
              { label: "Slug", value: inst.slug },
              { label: "Hostname", value: inst.hostname },
              { label: "Instance id", value: inst.id },
              { label: "Organization id", value: inst.org },
              { label: "Created", value: `${inst.created} (${ago(inst.created)})` },
            ] as BnKvRow[]}
            unsetText="not set"
          ></bn-kv>
          <p class="muted" style="margin-top:var(--bino-space-md)">
            The name is the organization-facing label and is freely renameable. The slug is the
            single DNS label under the zone that forms the hostname, and it is unique across the
            whole zone, so renaming the instance does not move the endpoint.
          </p>
        </bn-card>

        <bn-card heading="Image and version">
          <bn-kv
            .rows=${[
              { label: "Sluice version", value: inst.sluice_version },
              { label: "Pinned image digest", value: inst.image_digest },
            ] as BnKvRow[]}
          ></bn-kv>
          <p style="margin-top:var(--bino-space-md)">
            The image is published by Sluice Cloud and deployed <strong>by digest</strong>, never by
            tag. It is never customer-supplied, and a version outside the published allowlist is
            refused rather than pulled. Changing the pinned version replaces the running process:
            expect a service interruption of roughly 10 to 25 seconds.
          </p>
          <div class="row" style="margin-top:var(--bino-space-md)">
            <bn-button size="sm" disabled>Change version</bn-button>
            <bn-badge variant="not-enforced">Not in this build</bn-badge>
          </div>
          <p class="muted">
            Per-instance upgrade with rollback is a later release. Until it lands the version is
            changed by support, so that a failed upgrade has a person attached to it rather than a
            self-service button.
          </p>
        </bn-card>

        <bn-card heading="Capacity">
          <bn-kv .rows=${[{ label: "Size class", value: inst.size }] as BnKvRow[]}></bn-kv>
          <p style="margin-top:var(--bino-space-md)">
            Size is one knob. The container memory limit, the query engine's own memory limit, its
            thread count, the process limit and the scheduling reservations are all derived from it
            together — setting them independently is how a container gets killed instead of spilling
            to disk. There is no replica control, here or anywhere.
          </p>
          <p class="muted">
            Editing the size and the display name lives on the
            <a class="inline" href=${hrefFor("instances", this.instanceId, "settings")}
              >instance settings page</a
            >, because a size change is a rolling restart and belongs behind the same blast-radius
            statement as any other restart-class change.
          </p>
        </bn-card>

        <bn-card heading="Audit privacy profile">
          <bn-kv
            .rows=${[{ label: "Profile", value: inst.audit_profile }] as BnKvRow[]}
          ></bn-kv>
          <p style="margin-top:var(--bino-space-md)">
            The profile decides what the audit pipeline is permitted to store, so it is
            restart-class: changing it replaces the running process. Standard records a bounded SQL
            sample; Minimized records none, and no later setting can recover text that was never
            written.
          </p>
          <p class="muted">
            Changing it is an owner action and carries the same restart acknowledgement as any
            server-config change.
          </p>
        </bn-card>
      </div>
    `;
  }

  /* ── danger zone (`SC-F317`, `SC-F318`, `SC-F916`) ─────────────────────── */

  private renderDanger(inst: Instance): TemplateResult {
    const suspended = this.lifecycle === "suspended";
    // The server refuses both on a failed instance (409 instance_not_ready).
    const failed = this.lifecycle === "failed";
    const gone = this.lifecycle === "deleting" || this.lifecycle === "deleted";

    return html`
      <div class="stack">
        ${this.actionError
          ? this.renderError("The last operation failed", this.actionError, undefined)
          : nothing}
        ${this.actionError?.code === "instance_busy"
          ? html`<bn-panel eyebrow="Serialized">
              <p>
                Exactly one mutating operation runs against an instance at a time, so that desired
                and observed state cannot diverge under concurrency.
                ${jobIdOf(this.actionError)
                  ? html`The holding job is
                      <a class="inline" href=${hrefFor("jobs", jobIdOf(this.actionError) ?? "")}
                        >${jobIdOf(this.actionError)}</a
                      >.`
                  : nothing}
              </p>
            </bn-panel>`
          : nothing}

        <div class="danger-card">
          <h2>Restart</h2>
          <p>
            Recreates the task by incrementing the force-update counter. The service spec is not
            otherwise touched, so a restart creates no generation and is not a publish.
          </p>
          <bn-button
            variant="danger"
            ?disabled=${!this.canRestart || gone || failed || this.busy !== null}
            ?loading=${this.busy === "restart"}
            @click=${() => (this.confirm = "restart")}
            >Restart instance</bn-button
          >
          ${!this.canRestart
            ? html`<p class="muted">Restarting needs the maintainer role on this organization.</p>`
            : nothing}
          ${failed
            ? html`<p class="muted">
                A failed instance cannot be restarted. Retry the first publish if it never ran,
                otherwise delete the instance.
              </p>`
            : nothing}
        </div>

        <div class="danger-card">
          <h2>${suspended ? "Resume" : "Suspend"}</h2>
          <p>
            ${suspended
              ? "Starts the instance's Pod again on the same data directory. The audit chain continues from its last record — no new genesis record is written."
              : "Deletes the instance's Pod. The Service, the Secrets and the data directory are retained; the endpoint stops answering."}
          </p>
          <bn-button
            variant="danger"
            ?disabled=${!this.canOwn || gone || failed || this.busy !== null}
            ?loading=${this.busy === "suspend" || this.busy === "resume"}
            @click=${() => (this.confirm = suspended ? "resume" : "suspend")}
            >${suspended ? "Resume instance" : "Suspend instance"}</bn-button
          >
          ${!this.canOwn
            ? html`<p class="muted">
                Suspending and resuming need the owner role on this organization.
              </p>`
            : nothing}
          ${failed
            ? html`<p class="muted">
                A failed instance cannot be suspended. Retry the first publish if it never ran,
                otherwise delete the instance.
              </p>`
            : nothing}
        </div>

        <div class="danger-card">
          <h2>Delete</h2>
          <p>
            Irreversible. Deletion is also not instant: the record moves to
            <strong>deleting</strong> and stays there while the reconciler converges the cluster and
            a node-local agent confirms every volume was reclaimed. Nothing claims the data is gone
            before that confirmation arrives.
          </p>
          <bn-button
            variant="danger"
            ?disabled=${!this.canOwn || gone || this.busy !== null}
            ?loading=${this.busy === "delete"}
            @click=${() => {
              this.deleteTyped = "";
              this.confirm = "delete";
            }}
            >Delete instance</bn-button
          >
          ${gone
            ? html`<p class="muted">
                This instance is already being deleted. Its progress is on the overview tab.
              </p>`
            : nothing}
          ${!this.canOwn
            ? html`<p class="muted">Deleting needs the owner role on this organization.</p>`
            : nothing}
        </div>
      </div>

      ${this.renderConfirm(inst)}
    `;
  }

  /**
   * Every destructive action states its cost before the confirm, in bn-modal's
   * `consequence` slot — which renders above the action row, so it is read
   * before the button is reachable.
   */
  private renderConfirm(inst: Instance): TemplateResult {
    const kind = this.confirm;
    if (!kind) return html``;

    if (kind === "delete") {
      const typedOk = this.deleteTyped.trim() === inst.name;
      return html`
        <bn-modal
          open
          tone="destructive"
          heading=${`Delete ${inst.name}?`}
          .width=${620}
          @close=${() => (this.confirm = null)}
        >
          <p><strong>Destroyed, and not recoverable:</strong></p>
          <ul>
            <li>the instance's Pod, its Service and its Secrets</li>
            <li>
              the data directory on the instances claim — the local audit segments not yet ingested,
              the approval store and the budget store
            </li>
            <li>the hostname's route; the slug stays reserved so it cannot be reissued</li>
          </ul>
          <p><strong>Retained:</strong></p>
          <ul>
            <li>every audit record already ingested, searchable for the configured retention</li>
            <li>the generation history and the sealed genesis seed, so the export stays verifiable</li>
          </ul>
          <bn-field
            label=${`Type the instance name to confirm`}
            hint=${`Enter ${inst.name} exactly.`}
            required
          >
            <bn-input
              mono
              .value=${this.deleteTyped}
              @input=${(e: CustomEvent<{ value: string }>) => (this.deleteTyped = e.detail.value)}
            ></bn-input>
          </bn-field>
          <p slot="consequence">
            Deletion is asynchronous and it is not complete when the Pod disappears. The record
            moves to <strong>deleting</strong>, the reconciler converges the cluster, and only after
            the orchestrator confirms the data directory is removed does it become
            <strong>deleted</strong>. If that cannot be confirmed the record stays in deleting and
            says so, rather than showing you a completed deletion.
          </p>
          <bn-button slot="footer" @click=${() => (this.confirm = null)}>Cancel</bn-button>
          <bn-button
            slot="footer"
            variant="danger"
            ?disabled=${!typedOk}
            @click=${() => void this.runPower("delete")}
            >Delete this instance</bn-button
          >
        </bn-modal>
      `;
    }

    const copy = CONFIRMS[kind];
    return html`
      <bn-modal
        open
        tone="destructive"
        heading=${copy.heading(inst.name)}
        .width=${560}
        @close=${() => (this.confirm = null)}
      >
        <p>${copy.body}</p>
        <p slot="consequence">${copy.consequence}</p>
        <bn-button slot="footer" @click=${() => (this.confirm = null)}>Cancel</bn-button>
        <bn-button slot="footer" variant="danger" @click=${() => void this.runPower(kind)}
          >${copy.confirm}</bn-button
        >
      </bn-modal>
    `;
  }

  private renderVerifyModal(): TemplateResult {
    const sent = this.otpId !== "";
    const offerPasskey = this.hasPasskeys !== false;
    return html`<bn-modal
      ?open=${this.verifyOpen}
      tone="destructive"
      heading="Verify it is you"
      .width=${520}
      @close=${() => this.closeVerify()}
    >
      <p>
        ${
          sent
            ? "We sent a one-time code to your email address. Enter it to delete the instance."
            : "Deleting an instance cannot be undone, so it needs a fresh check that you are here. The proof is single-use and valid for five minutes."
        }
      </p>
      ${
        offerPasskey
          ? html`<div class="verify-branch">
                <bn-button
                  variant="secondary"
                  block
                  ?loading=${this.verifying}
                  @click=${() => void this.verifyByPasskey()}
                  >Verify with a passkey</bn-button
                >
              </div>
              <p class="verify-or">or</p>`
          : nothing
      }
      ${
        sent
          ? html`<bn-field label="One-time code" required>
              <bn-input
                mono
                inputmode="numeric"
                autocomplete="one-time-code"
                placeholder="123456"
                ?disabled=${this.verifying}
                .value=${this.otpCode}
                @input=${(e: CustomEvent<{ value: string }>) => (this.otpCode = e.detail.value)}
                @keydown=${(e: KeyboardEvent) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  void this.confirmCode();
                }}
              ></bn-input>
            </bn-field>`
          : nothing
      }
      <bn-button slot="footer" variant="ghost" @click=${() => this.closeVerify()}>Cancel</bn-button>
      ${
        sent
          ? html`<bn-button
              slot="footer"
              variant="danger"
              ?loading=${this.verifying}
              ?disabled=${this.otpCode.trim() === ""}
              @click=${() => void this.confirmCode()}
              >Verify and delete</bn-button
            >`
          : html`<bn-button
              slot="footer"
              variant="primary"
              ?loading=${this.verifying}
              @click=${() => void this.sendCode()}
              >Email me a code</bn-button
            >`
      }
    </bn-modal>`;
  }

  /* ── render ────────────────────────────────────────────────────────────── */

  override render() {
    if (this.loading) {
      return html`<bn-spinner block size="28" label="Loading instance"></bn-spinner>`;
    }
    if (this.loadError) {
      return this.renderError("This instance could not be loaded", this.loadError, () =>
        void this.load(),
      );
    }
    const inst = this.instance;
    if (!inst) {
      return html`<bn-empty-state
        icon="∅"
        heading="No instance"
        message="This console was opened without an instance id."
      ></bn-empty-state>`;
    }

    return html`
      ${this.renderHeader(inst)}
      <bn-tabs
        label="Instance console"
        .tabs=${TABS}
        .active=${this.tab}
        @change=${(e: CustomEvent<BnTabsChangeDetail>) => this.selectTab(e.detail.id)}
      >
        <div slot="overview">${this.tab === "overview" ? this.renderOverview(inst) : nothing}</div>
        <div slot="tasks">${this.tab === "tasks" ? this.renderTasks() : nothing}</div>
        <div slot="tail">${this.tab === "tail" ? this.renderTail(inst) : nothing}</div>
        <div slot="settings">${this.tab === "settings" ? this.renderSettings(inst) : nothing}</div>
        <div slot="danger">${this.tab === "danger" ? this.renderDanger(inst) : nothing}</div>
      </bn-tabs>
      ${this.renderVerifyModal()}
    `;
  }
}

/* ── module-local tables ──────────────────────────────────────────────────── */

/** The job types that hold the per-instance lock. The `mutating` column is not
 * on the wire, so the set is restated from `JobType.Mutating`. */
function holdsLock(type: string): boolean {
  return type.startsWith("instance.") || type === "config.publish" || type === "config.rollback";
}

const POWER_LABELS: Record<"restart" | "suspend" | "resume" | "delete" | "retry", string> = {
  retry: "Retry",
  restart: "Restart",
  suspend: "Suspend",
  resume: "Resume",
  delete: "Deletion",
};

const POWER_DONE: Record<"restart" | "suspend" | "resume" | "delete" | "retry", string> = {
  retry: "The instance is running.",
  restart: "The task was recreated.",
  suspend: "The instance is suspended and is no longer serving queries.",
  resume: "The instance is starting again.",
  delete: "Deletion started. The record stays in deleting until every volume is reclaimed.",
};

interface ConfirmCopy {
  heading: (name: string) => string;
  body: string;
  consequence: string;
  confirm: string;
}

/** The blast-radius statements. Numbers come from the measured availability
 * contract (`02` §5.5) and must move with it, not drift here. */
const CONFIRMS: Record<"restart" | "suspend" | "resume", ConfirmCopy> = {
  restart: {
    heading: (n) => `Restart ${n}?`,
    body: "The Pod is replaced by incrementing the restart counter. Nothing else about the Pod changes, so this produces no new generation and nothing about your configuration changes.",
    consequence:
      "The instance stops answering for roughly 10 to 25 seconds. Queries already running are terminated after approximately 10 seconds — the drain is fixed and cannot be lengthened. Your instance runs one replica; it is not highly available, and there is no second task to take the traffic.",
    confirm: "Restart now",
  },
  suspend: {
    heading: (n) => `Suspend ${n}?`,
    body: "The instance's Pod is deleted. The Service, the Secrets and the data directory are all retained, and resuming starts a Pod on the same directory so the audit chain continues from its last record.",
    consequence:
      "The instance stops serving queries entirely and its hostname stops answering, until you resume it. It is excluded from running-instance counts but storage continues to bill.",
    confirm: "Suspend now",
  },
  resume: {
    heading: (n) => `Resume ${n}?`,
    body: "A Pod starts again on the same node, on the same data directory. No new genesis record is written; the audit chain continues.",
    consequence:
      "Starting takes up to a few minutes, and readiness is fail-closed until the first datasource probe completes, so the endpoint answers 503 for a short window after the task starts.",
    confirm: "Resume now",
  },
};

/** Job status → badge tone. `cancelled` is neutral: a cancelled job is an
 * outcome the reader chose, not a failure. */
function jobVariant(status: Job["status"]): BnBadgeVariant {
  switch (status) {
    case "succeeded":
      return "success";
    case "failed":
    case "timed_out":
      return "error";
    case "running":
      return "info";
    case "cancelled":
      return "neutral";
    default:
      return "neutral";
  }
}

/** Swarm task state → badge tone. The strings come from the engine, so anything
 * unrecognized stays neutral rather than being guessed at. */
function taskVariant(state: string): BnBadgeVariant {
  switch (state) {
    case "running":
      return "success";
    case "failed":
    case "rejected":
      return "error";
    case "pending":
    case "starting":
    case "preparing":
    case "assigned":
    case "accepted":
      return "info";
    case "shutdown":
    case "complete":
      return "neutral";
    default:
      return "neutral";
  }
}

/** `instance_busy` carries the holding job's id, which is the only useful thing
 * to say about it (`06` §1.4.1). */
function jobIdOf(err: ApiError): string | undefined {
  return errorDetail<string>(err, "job_id");
}

declare global {
  interface HTMLElementTagNameMap {
    "page-instance": PageInstance;
  }
}
